// All calculator logic. Browser-only, no API calls.

// Convert kg <-> lbs
export const kgToLbs = (kg) => kg * 2.2046226218;
export const lbsToKg = (lbs) => lbs / 2.2046226218;

// Convert ml <-> floz (US)
export const mlToFloz = (ml) => ml / 29.5735296;
export const flozToMl = (floz) => floz * 29.5735296;

// ml <-> liters
export const mlToL = (ml) => ml / 1000;
// floz <-> US cups (8 floz)
export const flozToCups = (floz) => floz / 8;

/**
 * Daily water intake (ml) using a hybrid of bodyweight + activity + climate adjustments.
 * Base: 35 ml per kg of body weight (commonly cited adult guideline).
 * Activity adjustments add fluid loss replacement.
 * Climate adjustments add for heat/humidity.
 * Age adjustment reduces baseline for elderly (kidney concentration).
 * Pregnancy/breastfeeding adds fixed mL.
 */
export function calculateDailyWaterMl({
    weightKg,
    activityLevel = "moderate",
    climate = "temperate",
    age = 30,
    pregnant = false,
    breastfeeding = false,
}) {
    if (!weightKg || weightKg <= 0) return 0;

    let baseMl = weightKg * 35;

    // Age tweaks
    if (age < 18) baseMl = weightKg * 40; // children/teens need slightly more per kg
    if (age >= 65) baseMl = weightKg * 30; // elderly slight reduction

    // Activity multipliers (additive ml)
    const activityAdds = {
        sedentary: 0,
        light: 250,
        moderate: 500,
        active: 750,
        athlete: 1100,
    };
    baseMl += activityAdds[activityLevel] ?? 500;

    // Climate adjustments
    const climateAdds = {
        cold: -100,
        temperate: 0,
        warm: 250,
        hot: 500,
        humid: 600,
    };
    baseMl += climateAdds[climate] ?? 0;

    // Pregnancy / breastfeeding (per IOM guidance)
    if (pregnant) baseMl += 300;
    if (breastfeeding) baseMl += 700;

    // Sensible bounds
    if (baseMl < 1200) baseMl = 1200;
    if (baseMl > 5000) baseMl = 5000;

    return Math.round(baseMl);
}

// Workout-specific extra fluid loss (ml/min of moderate-vigorous activity)
// Roughly 12 ml/min for moderate, 20 ml/min for vigorous (per ACSM-style estimates).
export function calculateWorkoutFluidMl({ minutes = 30, intensity = "moderate" }) {
    const rate = intensity === "vigorous" ? 20 : intensity === "light" ? 7 : 12;
    return Math.round(minutes * rate);
}

// Kids: weight-based formula (Holliday-Segar): first 10kg=100ml/kg, next 10=50ml/kg, >20=20ml/kg
export function calculateKidWaterMl(weightKg) {
    if (!weightKg || weightKg <= 0) return 0;
    let ml = 0;
    if (weightKg <= 10) ml = weightKg * 100;
    else if (weightKg <= 20) ml = 1000 + (weightKg - 10) * 50;
    else ml = 1500 + (weightKg - 20) * 20;
    return Math.round(ml);
}

// Elderly suggestion: 30 ml/kg, capped 1500-2500
export function calculateElderlyWaterMl(weightKg) {
    if (!weightKg || weightKg <= 0) return 0;
    let ml = weightKg * 30;
    if (ml < 1500) ml = 1500;
    if (ml > 2500) ml = 2500;
    return Math.round(ml);
}

// Build hourly schedule from wake to bed
export function buildHourlySchedule(totalMl, wakeHour = 7, bedHour = 22) {
    const hoursAwake = Math.max(1, bedHour - wakeHour);
    const perHour = Math.round(totalMl / hoursAwake);
    const schedule = [];
    for (let h = wakeHour; h <= bedHour; h++) {
        schedule.push({
            hour: h,
            label: formatHour(h),
            ml: perHour,
        });
    }
    return schedule;
}

export function formatHour(h) {
    const period = h >= 12 ? "PM" : "AM";
    const hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh}:00 ${period}`;
}

// Glasses representation (1 glass = 250ml)
export function calculateGlasses(totalMl, glassMl = 250) {
    return Math.max(1, Math.round(totalMl / glassMl));
}

export function formatVolume(ml, units) {
    if (units === "metric") {
        if (ml >= 1000) return `${(ml / 1000).toFixed(2)} L`;
        return `${ml} ml`;
    }
    const oz = mlToFloz(ml);
    return `${oz.toFixed(1)} fl oz`;
}
